import { createClient, SupabaseClient } from '@supabase/supabase-js'

// Get environment variables - REQUIRED for production (Vercel)
// IMPORTANT: Vite requires VITE_ prefix (NOT NEXT_PUBLIC_)
// These must be set in Vercel dashboard: Settings > Environment Variables
// Variable names MUST be: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Check if user mistakenly used NEXT_PUBLIC_ prefix (Next.js prefix won't work with Vite)
const hasNextPublicUrl = !!(import.meta.env as any).NEXT_PUBLIC_SUPABASE_URL
const hasNextPublicKey = !!(import.meta.env as any).NEXT_PUBLIC_SUPABASE_ANON_KEY

// Debug logging for both development and production
// This helps diagnose issues in Vercel deployment
const isDev = import.meta.env.DEV
const isProduction = import.meta.env.PROD

if (isDev) {
  console.log('🔧 Development Mode - Supabase Config:')
  console.log('  URL:', supabaseUrl ? '✅ Found' : '❌ Missing')
  console.log('  Anon Key:', supabaseAnonKey ? `✅ Found (${supabaseAnonKey.substring(0, 20)}...)` : '❌ Missing')
  console.log('  Using env vars:', !!supabaseUrl && !!supabaseAnonKey)
}

// Production logging (will appear in browser console)
if (isProduction) {
  console.log('🚀 Production Mode - Supabase Config Check:')
  console.log('  URL:', supabaseUrl ? '✅ Loaded from Vercel env vars' : '❌ NOT SET IN VERCEL!')
  console.log('  Anon Key:', supabaseAnonKey ? '✅ Loaded from Vercel env vars' : '❌ NOT SET IN VERCEL!')
  
  // Check for wrong prefix (NEXT_PUBLIC_ instead of VITE_)
  if ((hasNextPublicUrl || hasNextPublicKey) && (!supabaseUrl || !supabaseAnonKey)) {
    console.error('❌ WRONG VARIABLE PREFIX DETECTED!')
    console.error('   You are using NEXT_PUBLIC_ prefix, but this is a VITE project!')
    console.error('   Vite requires VITE_ prefix to expose variables to the client.')
    console.error('   Fix in Vercel:')
    console.error('   1. Delete: NEXT_PUBLIC_SUPABASE_URL')
    console.error('   2. Delete: NEXT_PUBLIC_SUPABASE_ANON_KEY')
    console.error('   3. Add: VITE_SUPABASE_URL (with same value)')
    console.error('   4. Add: VITE_SUPABASE_ANON_KEY (with same value)')
    console.error('   5. Redeploy your site')
  } else if (!supabaseUrl || !supabaseAnonKey) {
    console.error('❌ CRITICAL: Supabase environment variables are missing!')
    console.error('   Please add them in Vercel: Settings > Environment Variables')
    console.error('   Required variables (with VITE_ prefix):')
    console.error('     - VITE_SUPABASE_URL')
    console.error('     - VITE_SUPABASE_ANON_KEY')
    console.error('   Note: This is a Vite project, NOT Next.js!')
  }
}

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  const errorMessage = isProduction 
    ? 'Missing Supabase environment variables. Please configure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in Vercel dashboard.'
    : 'Missing Supabase environment variables. Please create a .env file with VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
  
  console.error('❌', errorMessage)
  throw new Error(errorMessage)
}

// Create Supabase client - simplified configuration
export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
  },
  db: {
    schema: 'public',
  },
  global: {
    headers: {
      'x-client-info': 'branding-outloud-web',
    },
  },
})

